import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  type ResolvedTheme,
  type ThemePreference,
  applyTheme,
  readStoredTheme,
  resolveTheme,
  writeStoredTheme,
} from "./themeStorage";

export interface ThemeContextValue {
  /** What the user chose: `light`, `dark`, or `system`. */
  readonly theme: ThemePreference;
  /** What is actually painted, with `system` already resolved. */
  readonly resolvedTheme: ResolvedTheme;
  readonly setTheme: (theme: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/**
 * Reads the colour-scheme preference from context. Throws when used outside
 * {@link ThemeProvider} so consumers never silently render against a default.
 */
export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (value === null) {
    throw new Error("useTheme must be used within a ThemeProvider.");
  }
  return value;
}

/**
 * Owns the colour-scheme preference: persists it, resolves `system` against the
 * OS, and keeps the `.dark` class on <html> in sync.
 *
 * The class is already set before first paint by the inline script in
 * index.html; this provider takes over from there, so there is no flash on load
 * and no flash when the preference changes.
 *
 * Mounted at the root rather than inside the auth gate: the theme is a device
 * preference, and the sign-in and error screens rendered outside the gate should
 * honour it too.
 */
export function ThemeProvider({
  children,
}: {
  readonly children: ReactNode;
}): ReactNode {
  const [theme, setThemeState] = useState<ThemePreference>(readStoredTheme);
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() =>
    resolveTheme(theme),
  );

  useEffect(() => {
    applyTheme(resolvedTheme);
  }, [resolvedTheme]);

  // Only while following the system does the OS scheme still matter; an explicit
  // light/dark choice must not be overridden when the user's OS flips at sunset.
  useEffect(() => {
    if (theme !== "system") {
      return;
    }
    const query = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (event: MediaQueryListEvent): void => {
      setResolvedTheme(event.matches ? "dark" : "light");
    };
    query.addEventListener("change", onChange);
    return () => {
      query.removeEventListener("change", onChange);
    };
  }, [theme]);

  const setTheme = useCallback((next: ThemePreference): void => {
    writeStoredTheme(next);
    setThemeState(next);
    setResolvedTheme(resolveTheme(next));
  }, []);

  const value = useMemo(
    () => ({ theme, resolvedTheme, setTheme }),
    [theme, resolvedTheme, setTheme],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}
